import { Link } from 'react-router';

import { cn } from '@mds/shared/lib/utils';
import { typedLength, typingTime } from '@mds/shared/lib/typing';
import { useInView } from '@mds/shared/hooks/use-in-view';
import { useBreakpoint } from '@mds/shared/hooks/use-media-query';
import { useReducedMotion } from '@mds/shared/hooks/use-reduced-motion';
import { useStepTimer } from '@mds/shared/hooks/use-step-timer';
import { Button } from '@mds/shared/ui/shadcn/button';
import {
  ImageCarousel,
  type CarouselImage,
  type ImageCarouselProps,
} from '@mds/shared/ui/image/image-carousel';
import { Typewriter } from '@mds/shared/ui/typewriter';

import RuledList from '@/components/section/ruled-list';

export type StepShowcaseStep = {
  /** The step's name, "Book". Also what its dot is called on a phone: "Show Book". */
  title: string;
  /** One sentence, typed out while the step is on. */
  description: string;
};

export type StepShowcaseProps = {
  eyebrow?: string;
  title: string;
  description?: string;
  steps: StepShowcaseStep[];
  /** Names the list of steps for a screen reader, "How booking works". */
  label: string;
  /**
   * The photographs. From `lg`, all of them, crossfading on their own timer
   * beside the list. On a phone, one per step, in order, moving with the
   * steps; fewer photos than steps are reused from the first. Until they
   * arrive, the panel shows its ground.
   */
  images: ImageCarouselProps['images'];
  /** Names the photos for a screen reader, "Coworking at MDS". */
  galleryLabel: string;
  cta?: { label: string; to: string };
  headingLevel?: 2 | 3;
  className?: string;
};

/**
 * How long a step holds once its sentence is typed, so it can be read before
 * the next one starts.
 */
const HOLD = 2500;

/**
 * A short sequence explained a step at a time, advancing on its own: the
 * chosen step's sentence types itself out, holds, and the next step opens.
 *
 * Two layouts of the same steps on the same clock:
 *
 *   from `lg`   the numbered list beside a photo carousel, one step's
 *               sentence open in the list at a time
 *   phone       no list: one photo per step, with the step written over it,
 *               and the photo moving with the step
 *
 * The phone drops the list because stacked under the photo it made a band of
 * a screen and a half for three short sentences; on the photo, the band is
 * one panel. The dots on the photo say there are more steps, and a swipe or a
 * dot moves them by hand.
 *
 * One clock (`useStepTimer`) drives both, and each step lasts as long as its
 * sentence takes to type plus `HOLD`, so a long sentence is not cut short. It
 * runs only while the section is on screen, and not at all on reduced motion,
 * where every sentence shows whole.
 */
export default function StepShowcase({
  eyebrow,
  title,
  description,
  steps,
  label,
  images,
  galleryLabel,
  cta,
  headingLevel = 2,
  className,
}: StepShowcaseProps) {
  const Heading = headingLevel === 3 ? 'h3' : 'h2';
  const StepHeading = headingLevel === 3 ? 'h4' : 'h3';
  const wide = useBreakpoint('lg');
  const reducedMotion = useReducedMotion();
  const [inViewRef, inView] = useInView<HTMLDivElement>();

  const timer = useStepTimer({
    durations: steps.map((step) => typingTime(step.description) + HOLD),
    playing: inView && !reducedMotion,
  });

  // The typed part of a step's sentence: the chosen step types, every other
  // one stays whole (a closing one fades out as it was, not emptying).
  const typewriter = (i: number) => {
    const text = steps[i]!.description;
    const typing = i === timer.index && !reducedMotion;
    return (
      <Typewriter
        text={text}
        typed={typing ? typedLength(text, timer.elapsed) : text.length}
        caret={typing}
      />
    );
  };

  // A phone's photos: exactly one per step, named after it for its dot.
  const stepImages: CarouselImage[] =
    images.length > 0
      ? steps.map((step, i) => {
          const image = images[i % images.length]!;
          return { ...(typeof image === 'string' ? { src: image } : image), label: step.title };
        })
      : [];

  return (
    // One set of parts, laid out by the grid. On a phone, one column: the
    // heading, the photo panel, the button. From `lg`, the copy and the list
    // down the left and the photo spanning every row on the right, as tall as
    // the copy.
    <div
      ref={inViewRef}
      className={cn(
        'grid content-start gap-6 lg:grid-cols-2 lg:grid-rows-[auto_auto_1fr] lg:gap-x-16',
        className,
      )}
    >
      <div className="flex flex-col items-start gap-6">
        {eyebrow && (
          <span className="flex items-center gap-2.5 text-2xs font-semibold tracking-widest text-brand-text uppercase">
            <span aria-hidden="true" className="h-0.5 w-4.5 bg-brand-text" />
            {eyebrow}
          </span>
        )}
        <Heading className="text-3xl lg:text-4xl">{title}</Heading>
        {description && <p className="max-w-prose text-base text-ink-secondary">{description}</p>}
      </div>

      {/* A fixed height on a phone, so the panel does not change size as the
          steps change; from `lg` it takes the rows' height. */}
      <div className="relative h-110 overflow-hidden rounded-card bg-panel lg:col-start-2 lg:row-span-3 lg:row-start-1 lg:h-auto">
        {wide ? (
          // The same edge-to-edge crossfade as `FeatureSplit`'s bled photo,
          // on its own timer: beside the list the photos show the place, not
          // the step.
          <ImageCarousel
            variant="fill"
            label={galleryLabel}
            images={images}
            className="absolute inset-0"
          />
        ) : (
          <ImageCarousel
            variant="fill"
            label={galleryLabel}
            images={stepImages}
            index={timer.index}
            onIndexChange={timer.go}
            progress={reducedMotion ? undefined : timer.progress}
            className="absolute inset-0"
          >
            {/* The panel fading up from the foot, so the white words are on
                the ground they were paired with whatever the photo does. The
                bottom padding keeps the last line clear of the dots. */}
            <div
              data-ground="panel"
              className="flex h-full flex-col justify-end bg-linear-to-b from-transparent from-40% to-panel/85 px-card pb-14 text-on-panel"
            >
              {/* Every step in the same cell, so the panel is laid out for the
                  longest and nothing moves as they change. Only the chosen one
                  is read out: the rest are hidden from a screen reader as well
                  as from sight. Not announced while it runs on its own. */}
              <div aria-live={inView && !reducedMotion ? 'off' : 'polite'} className="grid">
                {steps.map((step, i) => {
                  const active = i === timer.index;
                  return (
                    <div
                      key={step.title}
                      aria-hidden={active ? undefined : true}
                      className={cn(
                        'col-start-1 row-start-1 transition-opacity duration-(--duration-base) ease-standard',
                        'motion-reduce:transition-none',
                        active ? 'opacity-100' : 'opacity-0',
                      )}
                    >
                      <StepHeading className="text-xl">{step.title}</StepHeading>
                      <p className="mt-2 max-w-[30ch] text-base text-on-panel/85">
                        {typewriter(i)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </ImageCarousel>
        )}
      </div>

      {wide && (
        <RuledList
          className="w-full"
          label={label}
          items={steps.map((step, i) => ({
            marker: String(i + 1).padStart(2, '0'),
            title: step.title,
            description: step.description,
          }))}
          activeIndex={timer.index}
          onSelect={timer.go}
          expand="active"
          selectOnHover={false}
          renderDescription={typewriter}
        />
      )}

      {cta && (
        // Full width on a phone, where it closes the band; its own width
        // beside the photo, where it is the end of a column of copy. Held to
        // the top of its row from `lg`, where that row takes the spare height.
        <Button
          asChild
          size="lg"
          className="mt-2 w-full lg:w-auto lg:self-start lg:justify-self-start"
        >
          <Link to={cta.to}>{cta.label}</Link>
        </Button>
      )}
    </div>
  );
}
